import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiArrowRight, FiCheck } from 'react-icons/fi';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/Button';
import { theme } from '../../styles/theme';

const CTASection = styled.section`
  padding: ${theme.spacing['5xl']} 0;
  background: ${theme.colors.gradients.hero};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
  text-align: center;
  position: relative;
  z-index: 2;
`;

const CTATitle = styled(motion.h2)`
  color: ${theme.colors.white};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.typography.sizes['4xl']};
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.sizes['3xl']};
  }
`;

const CTASubtitle = styled(motion.p)`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.typography.sizes.xl};
  margin-bottom: ${theme.spacing['2xl']};
  line-height: ${theme.typography.lineHeights.relaxed};
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.sizes.lg};
  }
`;

const WaitlistForm = styled(motion.form)`
  display: flex;
  gap: ${theme.spacing.md};
  max-width: 500px;
  margin: 0 auto ${theme.spacing.xl} auto;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.sizes.base};
  background: rgba(255, 255, 255, 0.95);
  color: ${theme.colors.gray[900]};
  
  &::placeholder {
    color: ${theme.colors.gray[500]};
  }
  
  &:focus {
    outline: none;
    background: ${theme.colors.white};
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
  }
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xl};
  backdrop-filter: blur(10px);
`;

const BenefitsList = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing['2xl']};
  text-align: left;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    text-align: center;
  }
`;

const BenefitItem = styled(motion.div)`
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.sm};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    justify-content: center;
  }
  
  svg {
    color: ${theme.colors.accent};
    flex-shrink: 0;
  }
`;

const FloatingShape = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  backdrop-filter: blur(10px);
`;

const Shape1 = styled(FloatingShape)`
  width: 200px;
  height: 200px;
  top: -100px;
  right: -100px;
`;

const Shape2 = styled(FloatingShape)`
  width: 150px;
  height: 150px;
  bottom: -75px;
  left: -75px;
`;

const Shape3 = styled(FloatingShape)`
  width: 100px;
  height: 100px;
  top: 50%;
  left: -50px;
  transform: translateY(-50%);
`;

export const CallToAction: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsLoading(false);
    setIsSubmitted(true);
    setEmail('');
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const benefits = [
    "Early access to the platform",
    "Exclusive beta testing opportunities",
    "Priority customer support",
  ];

  return (
    <CTASection id="cta">
      <Shape1
        animate={{
          rotate: [0, 360],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
      />
      <Shape2
        animate={{
          rotate: [0, -360],
          y: [0, -20, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <Shape3
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      <Container>
        <CTATitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Interested in implementing Care++ at your facility? 
        </CTATitle>
        
        <CTASubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Join our waitlist to be among the first to experience the future of 
          disability and aged care management.
        </CTASubtitle>
        
        <AnimatePresence mode="wait">
          {isSubmitted ? (
            <SuccessMessage
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <Icon icon={FiCheck} />
              Thank you! You've been added to our waitlist.
            </SuccessMessage>
          ) : (
            <WaitlistForm
              key="form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <EmailInput
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Button
                size="lg"
                variant="secondary"
                icon={isLoading ? undefined : <Icon icon={FiArrowRight} />}
                disabled={isLoading}
              >
                {isLoading ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </WaitlistForm>
          )}
        </AnimatePresence>
        
        <BenefitsList
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          {benefits.map((benefit, index) => (
            <BenefitItem
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
              viewport={{ once: true }}
            >
              <Icon icon={FiMail} />
              {benefit}
            </BenefitItem>
          ))}
        </BenefitsList>
      </Container>
    </CTASection>
  );
};